<!-- This component is used to display the payment step of the Inman OAP Enrollment Flow -->
<template>
	<section class="flex flex-col gap-4 px-6 py-10">
		<h1 class="ml-3 text-2xl font-bold">
			{{ t('oap.inmanPaymentAuthorization') }}
		</h1>
		<div
			v-if="isLoading"
			class="flex w-full items-center justify-center sm:h-47.5 xl:h-26.25"
		>
			<LoadingSVG class="ml-2" />
		</div>
		<div
			v-else-if="!isFormReady"
			class="flex items-center justify-center"
		>
			<div class="flex flex-col items-center justify-center gap-4">
				<ExclamationTriangleIcon class="h-10 w-10 text-red-400" />
				<strong>{{ t('document.fatalTitle') }}</strong>
				<p class="max-w-75 text-center text-slate-500">
					{{ t('document.fatalMessage') }}
				</p>
				<button
					type="button"
					class="rounded-lg border border-green-500 px-16 py-2 text-green-500"
					@click="loadForm"
				>
					{{ t('document.tryAgain') }}
				</button>
			</div>
		</div>
		<iframe
			v-show="isFormReady"
			ref="inmanForm"
			class="h-147.5 w-full border-0"
			:src="inmanFormUrl"
		></iframe>
	</section>

	<Teleport
		to="#oapEnrollmentNext"
		v-if="
			sessionStore.getPlanHolder(sessionDocumentsStore.currentPlanHolderID)
				?.oapStep === 'html'
		"
	>
		<button
			type="button"
			class="text-accent-700! h-12 w-52 justify-center rounded-md border-0 font-bold transition-all duration-300"
			:class="
				valid
					? 'bg-accent-400! hover:bg-accent-500! hover:text-accent-800!'
					: 'bg-secondary-300! hover:bg-secondary-300! cursor-not-allowed text-slate-500/50! hover:text-slate-500/50!'
			"
			@click="valid && sessionDocumentsStore.setOapStepTo('document')"
		>
			{{ t('document.authorizePayment') }}
		</button>
	</Teleport>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import LoadingSVG from '@/components/common/LoadingSVG.vue'
import { OapProvider } from '@/data/oapProviders'
import { usePresentationLocale } from '@/i18n'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { useAuthStore } from '@/stores/auth'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { InmanPaymentDetails, OapPayment } from '@/types'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'

const inmanForm = useTemplateRef<HTMLIFrameElement | null>('inmanForm')
const inmanFormUrl = ref<string>('')
const isFormReady = ref<boolean>(false)
const isLoading = ref<boolean>(true)
const valid = ref<boolean>(false)
const attempts = ref<number>(0)
const sessionStore = useSessionStore()
const oapPlanProvider = useOapPlanProvider(OapProvider.INMAN)
const { t } = usePresentationLocale()
const planStore = usePlanStore()
const authStore = useAuthStore()

const oapPayment = computed({
	get: () => {
		return sessionStore.getPlanHolder(sessionDocumentsStore.currentPlanHolderID)
			?.oapPayment
	},
	set: (value: OapPayment) => {
		planStore.updatePlan(sessionDocumentsStore.currentPlanHolderID, {
			oapPayment: value,
		} as Partial<Plan>)
	},
})
const purchasePageId = computed<string | undefined>(() => {
	return oapPlanProvider.getPricePointId()
})

const sessionDocumentsStore = useSessionDocumentsStore()
const planHolderId = computed<string | undefined>(() => {
	return sessionDocumentsStore.currentPlanHolderID
})

const loadForm = async () => {
	isLoading.value = true

	const authToken = await authStore.getAuthToken()

	inmanFormUrl.value =
		`${import.meta.env.VITE_PURCHASE_API_URL}/v1/oap/inman/form` +
		`?planId=${planHolderId.value}` +
		`&purchasePageId=${purchasePageId.value}` +
		`&pUserToken=${authToken}` +
		`&attempt=${++attempts.value}`
}

watch(
	planHolderId,
	() => {
		loadForm()
	},
	{ immediate: true }
)

onMounted(() => {
	oapPayment.value = {
		...oapPayment.value,
		provider: OapProvider.INMAN,
	} as OapPayment

	try {
		window.addEventListener('message', async (message) => {
			if (message.source !== inmanForm.value?.contentWindow) {
				return
			}

			switch (message.data.status) {
				case 'formReady':
					isLoading.value = false
					isFormReady.value = true
					break
				case 'success': {
					valid.value = true

					const dateToday = new Date().getDate()
					const paymentDayOfMonth = dateToday > 28 ? 1 : dateToday
					const paymentDetails: InmanPaymentDetails = {
						paymentDayOfMonth,
						confirmed: true,
						planType: 'Individual',
					}

					oapPayment.value = {
						...oapPayment.value,
						paymentPlan: paymentDetails,
					} as OapPayment
					break
				}
				case 'timeout':
					isLoading.value = false
			}
		})
	} catch (error) {
		console.error('Error initializing PaymentForm:', error)
	}
})
</script>
